import React from "react";
import { useSendAndConfirmTransaction } from "thirdweb/react";
import { toast } from "react-toastify";

/**
 * Drop-in replacement for thirdweb's <TransactionButton>.
 *
 * thirdweb's TransactionButton renders an internal error UI on a failed/
 * rejected transaction that can throw "Element type is invalid… got: undefined"
 * and crash the whole page. This wrapper sends the transaction via the
 * useSendAndConfirmTransaction hook and handles the result with toasts:
 *   - user rejects the wallet prompt  → quiet "Transaction cancelled" info toast
 *   - any other failure               → onError (if given) or an error toast
 * The render tree is never affected, so the app stays responsive.
 *
 * Same prop surface as TransactionButton (transaction / onTransactionConfirmed
 * / onError / disabled / className / children) so it can be swapped in by name.
 */
const isUserRejection = (e: any): boolean => {
  const msg = String((e && (e.message || e.reason)) || "").toLowerCase();
  const code = e && (e.code ?? (e.cause && e.cause.code));
  return (
    code === 4001 ||
    code === "ACTION_REJECTED" ||
    /user rejected|user denied|denied transaction|rejected the request|request rejected|user cancel/.test(msg)
  );
};

interface TxButtonProps {
  transaction: () => any;
  onTransactionConfirmed?: (receipt?: any) => void | Promise<void>;
  onError?: (error: any) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  style?: React.CSSProperties;
  // Accept (and harmlessly ignore) any other prop the original
  // <TransactionButton> took, e.g. onTransactionSent — keeps this a true drop-in.
  [key: string]: any;
}

export default function TxButton({
  transaction,
  onTransactionConfirmed,
  onError,
  disabled,
  className,
  children,
  style,
}: TxButtonProps) {
  const { mutateAsync, isPending } = useSendAndConfirmTransaction();

  const run = async () => {
    let prepared: any;
    try {
      prepared = transaction();
    } catch (e) {
      console.error(e);
      toast.error((e as any)?.message || "Could not prepare transaction");
      return;
    }
    try {
      const receipt = await mutateAsync(prepared);
      if (onTransactionConfirmed) await onTransactionConfirmed(receipt);
    } catch (e) {
      if (isUserRejection(e)) {
        toast.info("Transaction cancelled");
        return;
      }
      if (onError) onError(e);
      else {
        console.error(e);
        toast.error((e as any)?.message || "Transaction failed");
      }
    }
  };

  return (
    <button type="button" onClick={run} disabled={disabled || isPending} className={className} style={style} aria-busy={isPending}>
      {isPending ? "Processing…" : children}
    </button>
  );
}
